"use client";

import { useState, type FormEvent, type ReactNode } from "react";
import { ArrowRight, CheckCircle2, Loader2, X } from "lucide-react";
import { useLandingLang } from "@/lib/landingI18n";
import { trackEvent } from "@/lib/analyticsClient";

const DIGITAL_EMPLOYEE_OPTIONS = [
  "Chatbot",
  "Marketing",
  "Sales",
  "Operations Manager",
  "Human Resources",
  "Legal Assistant",
  "Customer Support",
  "Finance Assistant",
];

interface LeadForm {
  name: string;
  phone: string;
  email: string;
  company: string;
  industry: string;
  employeeCount: string;
  desiredEmployees: string[];
  desiredEmployeeOther: string;
  consent: boolean;
}

const EMPTY_FORM: LeadForm = {
  name: "",
  phone: "",
  email: "",
  company: "",
  industry: "",
  employeeCount: "",
  desiredEmployees: [],
  desiredEmployeeOther: "",
  consent: false,
};

export default function LeadCaptureButton({ children }: { children: string }) {
  const { lang } = useLandingLang();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<LeadForm>(EMPTY_FORM);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  function update<K extends keyof LeadForm>(key: K, value: LeadForm[K]) {
    setForm((current) => ({ ...current, [key]: value }));
  }

  function toggleDesiredEmployee(option: string) {
    setForm((current) => ({
      ...current,
      desiredEmployees: current.desiredEmployees.includes(option)
        ? current.desiredEmployees.filter((item) => item !== option)
        : [...current.desiredEmployees, option],
    }));
  }

  function validate() {
    const nextErrors: Record<string, string> = {};
    if (!form.name.trim()) nextErrors.name = "Нэрээ оруулна уу";
    if (!form.phone.trim() && !form.email.trim()) nextErrors.contact = "Утас эсвэл имэйлийн аль нэгийг оруулна уу";
    if (!form.company.trim()) nextErrors.company = "Байгууллагын нэрээ оруулна уу";
    if (!form.industry.trim()) nextErrors.industry = "Салбараа оруулна уу";
    if (form.desiredEmployees.length === 0 && !form.desiredEmployeeOther.trim()) {
      nextErrors.desiredEmployees = "Хүсэж буй дижитал ажилтнаа сонгоно уу";
    }
    if (!form.consent) nextErrors.consent = "Холбогдох зөвшөөрлийг баталгаажуулна уу";
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!validate() || submitting) return;

    setSubmitting(true);
    try {
      const response = await fetch("/api/leads", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, lang, source: "landing" }),
      });
      if (!response.ok) throw new Error("Lead submit failed");
      trackEvent("lead_submit", { source: "landing" });
      setSubmitted(true);
      setForm(EMPTY_FORM);
    } catch {
      setErrors({ submit: "Илгээж чадсангүй. Дахин оролдоно уу." });
    } finally {
      setSubmitting(false);
    }
  }

  function close() {
    setOpen(false);
    setErrors({});
    if (submitted) setSubmitted(false);
  }

  return (
    <>
      <button
        type="button"
        onClick={() => {
          trackEvent("lead_form_open");
          setOpen(true);
        }}
        className="inline-flex items-center gap-2 rounded-full bg-[var(--af-accent)] px-7 py-3.5 text-base font-semibold text-black transition hover:bg-[var(--af-accent-hover)]"
      >
        {children}
        <ArrowRight size={18} />
      </button>

      {open && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/45 p-4" onClick={close}>
          <div
            className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-[1.5rem] border border-[var(--af-border)] bg-[var(--af-bg)] p-5 text-left shadow-2xl sm:p-6"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-4">
              <div>
                <h3 className="text-xl font-semibold text-[var(--af-text)]">Дижитал ажилтан захиалах</h3>
                <p className="mt-1 text-sm text-[var(--af-muted)]">
                  Мэдээллээ үлдээгээрэй. Бид тохирох дижитал ажилтны саналтай холбогдоно.
                </p>
              </div>
              <button
                type="button"
                onClick={close}
                aria-label="Хаах"
                className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-[var(--af-muted)] transition hover:bg-[var(--af-bg-soft)] hover:text-[var(--af-text)]"
              >
                <X size={18} />
              </button>
            </div>

            {submitted ? (
              <div className="py-10 text-center">
                <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-[var(--af-accent)]/15 text-[var(--af-accent)]">
                  <CheckCircle2 size={28} />
                </div>
                <h4 className="mt-4 text-lg font-semibold text-[var(--af-text)]">Амжилттай илгээгдлээ</h4>
                <p className="mx-auto mt-2 max-w-sm text-sm text-[var(--af-muted)]">
                  Таны хүсэлт CRM-д бүртгэгдлээ. Манай баг удахгүй холбогдоно.
                </p>
                <button
                  type="button"
                  onClick={close}
                  className="mt-6 rounded-full bg-[var(--af-accent)] px-6 py-3 text-sm font-semibold text-black transition hover:bg-[var(--af-accent-hover)]"
                >
                  Болсон
                </button>
              </div>
            ) : (
              <form onSubmit={submit} className="mt-5 space-y-4">
                <div className="grid gap-4 sm:grid-cols-2">
                  <Field label="Нэр" error={errors.name}>
                    <input
                      value={form.name}
                      onChange={(event) => update("name", event.target.value)}
                      className="lead-input"
                      autoComplete="name"
                    />
                  </Field>
                  <Field label="Байгууллагын нэр" error={errors.company}>
                    <input
                      value={form.company}
                      onChange={(event) => update("company", event.target.value)}
                      className="lead-input"
                      autoComplete="organization"
                    />
                  </Field>
                </div>

                <div className="grid gap-4 sm:grid-cols-2">
                  <Field label="Утас" error={errors.contact}>
                    <input
                      value={form.phone}
                      onChange={(event) => update("phone", event.target.value)}
                      className="lead-input"
                      type="tel"
                      autoComplete="tel"
                    />
                  </Field>
                  <Field label="Имэйл" error={errors.contact}>
                    <input
                      value={form.email}
                      onChange={(event) => update("email", event.target.value)}
                      className="lead-input"
                      type="email"
                      autoComplete="email"
                    />
                  </Field>
                </div>

                <div className="grid gap-4 sm:grid-cols-2">
                  <Field label="Салбар" error={errors.industry}>
                    <input
                      value={form.industry}
                      onChange={(event) => update("industry", event.target.value)}
                      className="lead-input"
                      placeholder="Жишээ: худалдаа, боловсрол, үл хөдлөх"
                    />
                  </Field>
                  <Field label="Одоогийн ажилтны тоо">
                    <select
                      value={form.employeeCount}
                      onChange={(event) => update("employeeCount", event.target.value)}
                      className="lead-input"
                    >
                      <option value="">Сонгох</option>
                      <option value="1-5">1-5</option>
                      <option value="6-20">6-20</option>
                      <option value="21-50">21-50</option>
                      <option value="51-200">51-200</option>
                      <option value="200+">200+</option>
                    </select>
                  </Field>
                </div>

                <div>
                  <div className="mb-2 text-sm font-medium text-[var(--af-text)]">Хүсэж буй дижитал ажилтан</div>
                  <div className="flex flex-wrap gap-2">
                    {DIGITAL_EMPLOYEE_OPTIONS.map((option) => {
                      const active = form.desiredEmployees.includes(option);
                      return (
                        <button
                          key={option}
                          type="button"
                          onClick={() => toggleDesiredEmployee(option)}
                          aria-pressed={active}
                          className={`rounded-full border px-3.5 py-2 text-sm font-medium transition ${
                            active
                              ? "border-[var(--af-accent)] bg-[var(--af-accent)] text-black"
                              : "border-[var(--af-border)] bg-[var(--af-bg-soft)] text-[var(--af-muted)] hover:border-[var(--af-accent)] hover:text-[var(--af-text)]"
                          }`}
                        >
                          {option}
                        </button>
                      );
                    })}
                  </div>
                  <input
                    value={form.desiredEmployeeOther}
                    onChange={(event) => update("desiredEmployeeOther", event.target.value)}
                    className="lead-input mt-3"
                    placeholder="Өөр хүсэж буй ажилтнаа бичих"
                  />
                  {errors.desiredEmployees && <p className="mt-1 text-xs text-[#ef4444]">{errors.desiredEmployees}</p>}
                </div>

                <label className="flex items-start gap-2.5 text-sm text-[var(--af-text)]">
                  <input
                    type="checkbox"
                    checked={form.consent}
                    onChange={(event) => update("consent", event.target.checked)}
                    className="mt-0.5 h-4 w-4 shrink-0 accent-[var(--af-accent)]"
                  />
                  Надтай agenticforce баг энэ хүсэлтийн талаар холбогдохыг зөвшөөрч байна.
                </label>
                {errors.consent && <p className="-mt-3 text-xs text-[#ef4444]">{errors.consent}</p>}
                {errors.submit && <p className="text-sm text-[#ef4444]">{errors.submit}</p>}

                <button
                  type="submit"
                  disabled={submitting}
                  className="flex w-full items-center justify-center gap-2 rounded-full bg-[var(--af-accent)] px-7 py-3.5 text-base font-semibold text-black transition hover:bg-[var(--af-accent-hover)] disabled:cursor-not-allowed disabled:opacity-70"
                >
                  {submitting ? <Loader2 size={18} className="animate-spin" /> : <ArrowRight size={18} />}
                  CRM рүү илгээх
                </button>
              </form>
            )}
          </div>
        </div>
      )}
    </>
  );
}

function Field({ label, error, children }: { label: string; error?: string; children: ReactNode }) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-sm font-medium text-[var(--af-text)]">{label}</span>
      {children}
      {error && <p className="mt-1 text-xs text-[#ef4444]">{error}</p>}
    </label>
  );
}
